/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Card, Icon, InputAdornment, LinearProgress, Skeleton, TextField } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import colors from 'styles/tokens/colors';
import { monoFontSx } from 'styles/tokens/typography';
import { v2 as t, v2Density as d, v2Tones, V2_DISPLAY_FONT_FAMILY, V2_FONT_FAMILY } from 'styles/tokens/v2';
import { useIsReV2 } from '../../v2/parts';
import { useInReFilterBar } from './filterContext';
import { TONES } from './statusColors';

/*
 * UI v2: every widget below branches on the theme. The legacy branch is the
 * classic markup unchanged; v2 drops JetBrains Mono for tabular numbers in the
 * v2 font and uses the v2 tokens.
 */

const tabular = { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1' };

// The screens colour values with the classic semantic hex. Two of them fail
// AA on white in v2's lighter type, so v2 reads them as the tone's ink.
const V2_INK = {
  '#1F8A56': v2Tones.success.fg,
  '#BA1A1A': v2Tones.danger.fg,
  '#8A5300': v2Tones.warning.fg,
};

/** v2 text colour for a classic value colour (null → default text). */
export const v2Ink = (color) => (color ? V2_INK[String(color).toUpperCase()] || color : null);

const V2_TILE_TONE = { error: 'danger' };

/** KPI tile: label, big value, optional hint and tone accent. */
export function KpiTile({ label, value, hint, icon, tone, loading, onClick }) {
  const v2 = useIsReV2();
  if (v2) return <KpiTileV2 label={label} value={value} hint={hint} tone={tone} loading={loading} onClick={onClick} />;
  const t = TONES[tone] || TONES.info;
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: '12px',
        border: '1px solid #E8EDF5',
        boxShadow: 'none',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { borderColor: t.dot } : undefined,
      }}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <MDTypography variant="caption" fontWeight="bold" sx={{ color: colors.text.light, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </MDTypography>
        {icon && (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: t.background, color: t.text }}
          >
            <Icon sx={{ fontSize: '18px !important' }}>{icon}</Icon>
          </MDBox>
        )}
      </MDBox>
      {loading ? (
        <Skeleton variant="text" width="60%" height={36} />
      ) : (
        <MDTypography variant="h5" fontWeight="bold" sx={{ color: colors.text.dark, ...monoFontSx, fontWeight: 600 }}>
          {value}
        </MDTypography>
      )}
      {hint && (
        <MDTypography variant="caption" sx={{ color: colors.text.light }}>
          {hint}
        </MDTypography>
      )}
    </Card>
  );
}

KpiTile.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  hint: PropTypes.node,
  icon: PropTypes.string,
  tone: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
};
KpiTile.defaultProps = { value: '—', hint: null, icon: null, tone: 'info', loading: false, onClick: null };

/** v2 stat tile (design-reference stat cards): label, 28px tabular value, hint. */
function KpiTileV2({ label, value, hint, tone, loading, onClick }) {
  const accent = v2Tones[V2_TILE_TONE[tone] || tone] || null;
  const clickable = Boolean(onClick);
  return (
    <Box
      component={clickable ? 'button' : 'section'}
      type={clickable ? 'button' : undefined}
      onClick={onClick || undefined}
      sx={{
        display: 'block',
        width: '100%',
        height: '100%',
        textAlign: 'left',
        font: 'inherit',
        fontFamily: V2_FONT_FAMILY,
        p: 2.25,
        bgcolor: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: `${d.cardRadiusPx}px`,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { borderColor: t.border2 } : undefined,
        '&:focus-visible': { outline: `2px solid ${t.link}`, outlineOffset: 2 },
      }}
    >
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, color: t.text2 }}>
        {accent && tone !== 'neutral' && tone !== 'info' && (
          <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent.fg, flexShrink: 0 }} />
        )}
        {label}
      </Box>
      {loading ? (
        <Skeleton variant="text" width="60%" height={40} />
      ) : (
        <Box
          component="span"
          data-testid="re-kpi-value"
          sx={{ display: 'block', mt: 0.5, fontFamily: V2_DISPLAY_FONT_FAMILY, fontSize: 28, lineHeight: 1.2, fontWeight: 600, color: t.text, ...tabular }}
        >
          {value}
        </Box>
      )}
      {hint && (
        <Box component="span" sx={{ display: 'block', mt: 0.75, fontSize: 13, color: t.text3, ...tabular }}>
          {hint}
        </Box>
      )}
    </Box>
  );
}

KpiTileV2.propTypes = KpiTile.propTypes;
KpiTileV2.defaultProps = KpiTile.defaultProps;

/** Construction / payment progress with a numeric label. */
export function ProgressBar({ value, label, color }) {
  const isV2 = useIsReV2();
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  if (isV2) {
    return (
      <Box sx={{ fontFamily: V2_FONT_FAMILY }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
          <Box component="span" sx={{ fontSize: 13, color: t.text2 }}>{label}</Box>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: t.text, ...tabular }}>{`${v.toFixed(1)}%`}</Box>
        </Box>
        <Box
          role="progressbar"
          aria-valuenow={Number(v.toFixed(1))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={typeof label === 'string' ? label : undefined}
          sx={{ height: 6, borderRadius: 3, bgcolor: t.border, overflow: 'hidden' }}
        >
          <Box sx={{ width: `${v}%`, height: '100%', borderRadius: 3, bgcolor: v2Ink(color) || t.primary }} />
        </Box>
      </Box>
    );
  }
  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
        <MDTypography variant="caption" color="text">{label}</MDTypography>
        <MDTypography variant="caption" fontWeight="bold" sx={monoFontSx}>{`${v.toFixed(1)}%`}</MDTypography>
      </MDBox>
      <LinearProgress
        variant="determinate"
        value={v}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: '#E8EDF5',
          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: color || colors.brand.primary },
        }}
      />
    </MDBox>
  );
}

ProgressBar.propTypes = { value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), label: PropTypes.node, color: PropTypes.string };
ProgressBar.defaultProps = { value: 0, label: 'Progress', color: null };

/** Right-aligned tabular number/amount. */
export function Num({ children, bold, color }) {
  const isV2 = useIsReV2();
  if (isV2) {
    return (
      <Box component="span" sx={{ ...tabular, fontWeight: bold ? 600 : 400, color: v2Ink(color) || 'inherit', whiteSpace: 'nowrap' }}>
        {children}
      </Box>
    );
  }
  return (
    <MDBox component="span" sx={{ ...monoFontSx, fontWeight: bold ? 600 : 400, color: color || 'inherit', whiteSpace: 'nowrap' }}>
      {children}
    </MDBox>
  );
}

Num.propTypes = { children: PropTypes.node, bold: PropTypes.bool, color: PropTypes.string };
Num.defaultProps = { children: null, bold: false, color: null };

/** Native date input with the label shrunk (works under the 80% zoom). */
export function DateField({ label, value, onChange, size, sx, required, type, error, helperText, disabled, inputProps }) {
  const isV2 = useIsReV2();
  const inFilterBar = useInReFilterBar();
  if (isV2 && inFilterBar) {
    // v2 filter row: no floating label; the label is an inline prefix and the
    // accessible name, like FilterSelect's "Status: All".
    return (
      <TextField
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        // 172 fits the prefix and a dd/mm/yyyy date with its calendar icon.
        sx={{ width: 172, ...sx, minWidth: 0 }}
        required={required}
        error={Boolean(error)}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5, '& p': { fontSize: 14, color: t.text2, fontFamily: V2_FONT_FAMILY } }}>
              {label}
            </InputAdornment>
          ),
        }}
        inputProps={{ 'aria-label': label, ...inputProps }}
      />
    );
  }
  return (
    <TextField
      type={type}
      size={size}
      label={label}
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      InputLabelProps={{ shrink: true }}
      sx={sx}
      required={required}
      error={Boolean(error)}
      helperText={helperText}
      disabled={disabled}
      inputProps={inputProps}
    />
  );
}

DateField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.string,
  sx: PropTypes.object,
  required: PropTypes.bool,
  type: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  disabled: PropTypes.bool,
  inputProps: PropTypes.object,
};
DateField.defaultProps = {
  value: null, size: 'small', sx: { minWidth: 160 }, required: false, type: 'date', error: false, helperText: null,
  disabled: false, inputProps: undefined,
};

/** Label/value line used in summary panels. */
export function KV({ label, value, bold, color }) {
  const isV2 = useIsReV2();
  if (isV2) {
    // v2 label / value row: 13px muted label, 14px value, hairline between rows.
    return (
      <Box
        className="re-kv"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 2,
          py: 1,
          fontFamily: V2_FONT_FAMILY,
          '.re-kv + &': { borderTop: `1px solid ${t.border}` },
        }}
      >
        <Box component="span" sx={{ fontSize: 13, color: t.text2, minWidth: 0 }}>{label}</Box>
        <Box
          component="span"
          sx={{ fontSize: 14, fontWeight: bold ? 600 : 500, color: v2Ink(color) || t.text, textAlign: 'right', overflowWrap: 'anywhere', ...tabular }}
        >
          {value}
        </Box>
      </Box>
    );
  }
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="baseline" py={0.5} gap={2}>
      <MDTypography variant="button" color="text" fontWeight="regular">{label}</MDTypography>
      <MDTypography variant="button" fontWeight={bold ? 'bold' : 'regular'} sx={{ ...monoFontSx, color: color || colors.text.dark, textAlign: 'right' }}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

KV.propTypes = { label: PropTypes.node.isRequired, value: PropTypes.node, bold: PropTypes.bool, color: PropTypes.string };
KV.defaultProps = { value: '—', bold: false, color: null };
